import {
  Children,
  cloneElement,
  isValidElement,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type FocusEvent,
  type HTMLAttributes,
  type MouseEvent,
  type ReactElement,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../lib/utils.js';
import { portalHost } from '../lib/portalHost.js';
import { placeTooltip } from '../lib/tooltipPlacement.js';

interface TooltipProps {
  /** Tooltip body. Strings, JSX, anything. */
  content: ReactNode;
  /**
   * Element that triggers the tooltip — must accept an `aria-describedby`
   * attribute and the four pointer/focus event handlers.
   */
  children: ReactElement<HTMLAttributes<HTMLElement>>;
  /** Delay before showing on hover (ms). Default 200. */
  delay?: number;
  /** Preferred side. Defaults to `top`. */
  side?: 'top' | 'bottom' | 'left' | 'right';
  className?: string;
}

/**
 * Lightweight tooltip, PORTALLED and kept inside the window (owner,
 * 2026-10-01: a tip in a dialog was cut off at the dialog's edge — "fix this
 * everywhere"). It used to render as a sibling of its trigger, so any
 * ancestor that clips — a dialog, a card, a scrolling table — cut it off, and
 * nothing kept it on screen. It now draws on the portal host, `fixed`, placed
 * by `placeTooltip`: the side asked for if it fits, the opposite side if not,
 * clamped inside the window either way.
 *
 * The trigger receives an `aria-describedby` pointing at the tooltip body so
 * screen readers announce it as a description (not a label).
 */
export function Tooltip({
  content,
  children,
  delay = 200,
  side = 'top',
  className,
}: TooltipProps): JSX.Element {
  const id = useId();
  const [open, setOpen] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const anchorRef = useRef<HTMLSpanElement>(null);
  const tipRef = useRef<HTMLSpanElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  /* Measured before paint, so the tip never flashes at the wrong place. */
  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    const anchor = anchorRef.current;
    const tip = tipRef.current;
    if (!anchor || !tip) return;
    const a = anchor.getBoundingClientRect();
    const r = tip.getBoundingClientRect();
    const placed = placeTooltip({
      trigger: { top: a.top, left: a.left, width: a.width, height: a.height },
      width: r.width,
      height: r.height,
      side,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
    });
    setPos({ top: placed.top, left: placed.left });
  }, [open, side, content]);

  function show(): void {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setOpen(true), delay);
  }

  function hide(): void {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setOpen(false);
  }

  const child = Children.only(children);
  if (!isValidElement(child)) {
    throw new Error('<Tooltip> requires a single React element child.');
  }

  // Forward listeners + aria-describedby onto the child without losing any
  // handlers the consumer already attached.
  const childProps = child.props;
  const trigger = cloneElement<HTMLAttributes<HTMLElement>>(child, {
    'aria-describedby': open ? id : undefined,
    onMouseEnter: (e: MouseEvent<HTMLElement>) => {
      childProps.onMouseEnter?.(e);
      show();
    },
    onMouseLeave: (e: MouseEvent<HTMLElement>) => {
      childProps.onMouseLeave?.(e);
      hide();
    },
    onFocus: (e: FocusEvent<HTMLElement>) => {
      childProps.onFocus?.(e);
      show();
    },
    onBlur: (e: FocusEvent<HTMLElement>) => {
      childProps.onBlur?.(e);
      hide();
    },
  });

  return (
    <span ref={anchorRef} className="inline-flex">
      {trigger}
      {open &&
        createPortal(
          <span
            ref={tipRef}
            id={id}
            role="tooltip"
            /* Parked off-screen until measured — see the layout effect. */
            style={{ top: pos?.top ?? -9999, left: pos?.left ?? -9999 }}
            className={cn(
              // Inverted surface: `bg-foreground`/`text-background` is the one
              // pair guaranteed to stay legible in both themes without a
              // dedicated token, since they are each other's contrast partner.
              //
              // `w-max` sizes to the unwrapped text; `max-w-[16rem]` wraps a
              // long tip instead of letting it grow wider than a phone allows.
              // `fixed` on the portal host, and `z-50` like every portalled
              // surface here, so it draws over a dialog (see DatePopover).
              'pointer-events-none fixed z-50 w-max max-w-[16rem] rounded-xs bg-foreground px-2 py-1 text-copy-sm text-background shadow-popover',
              className
            )}
          >
            {content}
          </span>,
          portalHost()
        )}
    </span>
  );
}
